import React, { useEffect, useState } from "react";
import { Mail, Save, Eye, Send, RefreshCw, AlertTriangle, History, Inbox, CalendarClock, Ruler } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { Modal } from "../components/Modal";

// Sends that reach recall_sends with these `trigger` values belong to the
// Calibration Due flow (manual sends + the monthly scheduler) — everything
// else (job-received/non-conforming) is filtered out of this history table.
export const CAL_DUE_TRIGGERS = new Set([
  "manual_one_off", "manual_blast", "manual_selected", "manual_selected_all", "scheduled",
]);

// Friendly labels for `status`/`reason` values that aren't a plain
// sent/failed/no_recipient outcome, so the history table never shows a
// blank/confusing cell.
export const SEND_REASON_LABELS = {
  test_cap_skipped: "Daily test-send limit reached",
  test_daily_cap_reached: "Daily test-send limit reached",
  no_api_key: "Email service not configured",
  no_email: "No contact email on file",
  customer_not_found: "Customer not found",
  no_due_items: "No due items in window",
  unknown: "Skipped",
};

/**
 * Settings → Emails tab — container for every transactional template.
 * Sub-tabs:
 *   • Calibration Due — monthly batch + manual blast (full config + preview)
 *   • Job Received   — fires automatically on POST /api/jobs (template-only config)
 */
export default function RecallTab({ canSend }) {
  const [sub, setSub] = useState("calibration_due");
  return (
    <>
      <div className="ftabs" style={{ marginTop: 6 }}>
        <button className={`ftab ${sub === "calibration_due" ? "active" : ""}`}
                onClick={() => setSub("calibration_due")} data-testid="emails-sub-calibration-due">
          <CalendarClock size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Calibration Due
        </button>
        <button className={`ftab ${sub === "intake" ? "active" : ""}`}
                onClick={() => setSub("intake")} data-testid="emails-sub-intake">
          <Inbox size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Job Received
        </button>
        <button className={`ftab ${sub === "unserviceable" ? "active" : ""}`}
                onClick={() => setSub("unserviceable")} data-testid="emails-sub-unserviceable">
          <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Non-Conforming Notice
        </button>
        <button className={`ftab ${sub === "po_followup" ? "active" : ""}`}
                onClick={() => setSub("po_followup")} data-testid="emails-sub-po-followup">
          <CalendarClock size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          PO Follow-up
        </button>
        <button className={`ftab ${sub === "standards_due" ? "active" : ""}`}
                onClick={() => setSub("standards_due")} data-testid="emails-sub-standards-due">
          <Ruler size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Standards Due
        </button>
      </div>
      {sub === "calibration_due" && <CalibrationDueEmail canSend={canSend} />}
      {sub === "intake" && <IntakeEmail canSend={canSend} />}
      {sub === "unserviceable" && <UnserviceableEmail />}
      {sub === "po_followup" && <PoFollowupEmail canSend={canSend} />}
      {sub === "standards_due" && <StandardsDueEmail canSend={canSend} />}
    </>
  );
}


function CalibrationDueEmail({ canSend }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/recall/config"),
        api.get("/recall/sends", { params: { limit: 500, days: 30 } }),
      ]);
      setCfg(c.data);
      setHistory((h.data.items || []).filter((x) => CAL_DUE_TRIGGERS.has(x.trigger)));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/recall/config", {
        enabled: cfg.enabled,
        window_days: Number(cfg.window_days) || 61,
        day_of_month: Number(cfg.day_of_month) || 1,
        subject_template: cfg.subject_template,
        html_template: cfg.html_template,
        cc_email: cfg.cc_email || "",
        test_mode: !!cfg.test_mode,
        test_recipient: cfg.test_recipient || "",
      });
      setCfg(r.data);
      toast.success("Calibration Due email settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.get("/recall/preview", { params: { window_days: cfg.window_days } });
      setPreview(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setPreviewing(false); }
  };

  const sendBlast = async () => {
    setConfirmSend(false);
    setSending(true);
    try {
      const r = await api.post("/recall/send-blast", { window_days: cfg.window_days });
      const reasons = r.data.skip_reasons || {};
      const reasonText = Object.entries(reasons)
        .map(([k, n]) => `${SEND_REASON_LABELS[k] || k} (${n})`).join(", ");
      toast.success(
        `Blast complete · ${r.data.sent} sent · ${r.data.skipped} skipped${reasonText ? ` [${reasonText}]` : ""} · ${r.data.failed} failed`
      );
      load();
      setPreview(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  if (loading || !cfg) return <div className="card pad" style={{ marginTop: 16 }}><RefreshCw size={14} className="spin" /> Loading…</div>;

  const testBanner = cfg.test_mode && (
    <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", marginBottom: 14, fontSize: 13 }}>
      <AlertTriangle size={15} style={{ verticalAlign: "-3px", marginRight: 6 }} />
      <b>TEST MODE</b> — every Calibration Due email is redirected to <code>{cfg.test_recipient || "(not set)"}</code> instead of the real customer.
      Turn off "Test mode" below once you're ready to send to real customers.
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
      {testBanner}

      {/* === SCHEDULE & TEMPLATE === */}
      <div className="card pad">
        <div className="sec-h">
          <h3>Monthly Calibration Due schedule &amp; template</h3>
          <Mail size={18} color="var(--gold-deep)" />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
          When enabled, every month on the configured day the system emails a branded PDF Calibration Due list
          to every customer with at least one item due in the selected window. Per-customer recipient
          is the <b>primary contact</b> → falling back to <b>any active contact</b> → falling back to
          the <b>customer record's email</b>. Customers with no email anywhere are logged as
          "could not send" in the history below.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div className="fld">
            <label>Auto-send enabled</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
              <input type="checkbox" checked={!!cfg.enabled}
                     onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                     data-testid="recall-enabled"
                     style={{ width: 16, height: 16 }} />
              {cfg.enabled ? "Yes — runs on schedule" : "No — manual only"}
            </label>
          </div>
          <div className="fld">
            <label>Window (days)</label>
            <select className="input" value={cfg.window_days || 61}
                    onChange={(e) => setCfg({ ...cfg, window_days: Number(e.target.value) })}
                    data-testid="recall-window">
              <option value={31}>Next 31 days</option>
              <option value={61}>Next 61 days</option>
            </select>
          </div>
          <div className="fld">
            <label>Day of month (1–28)</label>
            <input type="number" className="input" min={1} max={28}
                   value={cfg.day_of_month || 1}
                   onChange={(e) => setCfg({ ...cfg, day_of_month: Number(e.target.value) })}
                   data-testid="recall-day-of-month" />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 14 }}>
          <div className="fld">
            <label>Test mode</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
              <input type="checkbox" checked={!!cfg.test_mode}
                     onChange={(e) => setCfg({ ...cfg, test_mode: e.target.checked })}
                     data-testid="recall-test-mode"
                     style={{ width: 16, height: 16 }} />
              {cfg.test_mode ? "On — redirects every send to the test recipient" : "Off — sends go to real customers"}
            </label>
          </div>
          <div className="fld">
            <label>Test recipient</label>
            <input className="input mono" value={cfg.test_recipient || ""}
                   onChange={(e) => setCfg({ ...cfg, test_recipient: e.target.value })}
                   placeholder="you@example.com" data-testid="recall-test-recipient" />
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Subject template</label>
          <input className="input" value={cfg.subject_template || ""}
                 onChange={(e) => setCfg({ ...cfg, subject_template: e.target.value })}
                 data-testid="recall-subject" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Placeholders: <code>{"{customer_name}"}</code>, <code>{"{contact_name}"}</code>, <code>{"{item_count}"}</code>, <code>{"{window_days}"}</code>, <code>{"{today}"}</code>.
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>CC (optional)</label>
          <input className="input mono" value={cfg.cc_email || ""}
                 onChange={(e) => setCfg({ ...cfg, cc_email: e.target.value })}
                 placeholder="heike@ptecal.co.za, elsa@ptecal.co.za" data-testid="recall-cc" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            One or more addresses, comma-separated. Copied on every real send (not applied while Test mode is on).
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>HTML body template</label>
          <textarea className="input mono" rows={10}
                    value={cfg.html_template || ""}
                    onChange={(e) => setCfg({ ...cfg, html_template: e.target.value })}
                    data-testid="recall-html"
                    style={{ fontSize: 12, lineHeight: 1.45 }} />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Inline CSS only (email clients ignore external styles). Same placeholders as subject.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn gold" onClick={save} disabled={saving} data-testid="recall-save">
            <Save size={13} /> {saving ? "Saving…" : "Save settings"}
          </button>
          <span className="t-mut" style={{ fontSize: 12, marginLeft: "auto" }}>
            Last run: {cfg.last_run_at ? fmtDate(cfg.last_run_at) : "never"}
            {cfg.last_run_summary && ` · ${cfg.last_run_summary.sent} sent · ${cfg.last_run_summary.failed} failed`}
          </span>
        </div>
      </div>

      {/* === PREVIEW & SEND === */}
      <div className="card pad">
        <div className="sec-h">
          <h3>Preview &amp; send</h3>
          <Eye size={18} color="var(--gold-deep)" />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
          Preview shows exactly which customers will receive a Calibration Due email and which contact each one resolves to. Nothing is sent until you click <b>Send now</b>.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <button className="btn" onClick={runPreview} disabled={previewing} data-testid="recall-preview">
            {previewing ? <><RefreshCw size={14} className="spin" /> Loading…</> : <><Eye size={14} /> Preview {cfg.window_days}-day recipients</>}
          </button>
          {canSend && (
            <button className="btn gold" onClick={() => setConfirmSend(true)}
                    disabled={sending || !preview || preview.will_send === 0}
                    data-testid="recall-send-blast">
              {sending ? <><RefreshCw size={14} className="spin" /> Sending…</> : <><Send size={14} /> Send now</>}
            </button>
          )}
        </div>

        {preview && (
          <div>
            <div style={{ display: "flex", gap: 18, fontSize: 13, marginBottom: 10, flexWrap: "wrap" }}>
              <span><b style={{ color: "var(--text-1)" }}>{preview.total_customers}</b> customers with due items</span>
              <span><b style={{ color: "var(--green)" }}>{preview.will_send}</b> will receive</span>
              <span><b style={{ color: preview.no_recipient ? "var(--red)" : "var(--text-1)" }}>{preview.no_recipient}</b> have no contact email</span>
              <span><b style={{ color: "var(--text-1)" }}>{preview.total_items}</b> total items</span>
            </div>
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Customer</th><th>Items</th><th>Recipient</th><th>Contact name</th><th>Source</th></tr></thead>
                <tbody>
                  {preview.customers.slice(0, 200).map((c) => (
                    <tr key={c.customer_id}>
                      <td><span className="t-strong">{c.customer_name}</span>{c.customer_number && <span className="t-mut" style={{ marginLeft: 6, fontSize: 11 }}>{c.customer_number}</span>}</td>
                      <td className="mono">{c.item_count}</td>
                      <td className="mono">{c.recipient_email || <span style={{ color: "var(--red)" }}>— none —</span>}</td>
                      <td>{c.recipient_name || "—"}</td>
                      <td><span className="t-mut" style={{ fontSize: 11 }}>{c.recipient_source}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.customers.length > 200 && (
              <div className="t-mut" style={{ fontSize: 12, marginTop: 6 }}>(showing first 200 of {preview.customers.length})</div>
            )}
          </div>
        )}
      </div>

      {/* === HISTORY === */}
      <div className="card pad">
        <div className="sec-h">
          <h3>Recent sends <span className="t-mut" style={{ fontWeight: 400, fontSize: 12.5 }}>(last 30 days · {history.length})</span></h3>
          <History size={18} color="var(--gold-deep)" />
        </div>
        {history.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>No Calibration Due emails sent in the last 30 days.</p>
        ) : (
          <div className="tbl-wrap" style={{ maxHeight: 480, overflowY: "auto" }}>
            <table>
              <thead><tr><th>When</th><th>Customer</th><th>Items</th><th>Window</th><th>Status</th><th>Recipient</th><th>Trigger</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`recall-history-row-${h.id}`}>
                    <td className="mono t-mut">{fmtDate(h.sent_at)}</td>
                    <td className="t-strong">{h.customer_name}</td>
                    <td className="mono">{h.item_count}</td>
                    <td className="mono">{h.window_days}d</td>
                    <td>
                      {h.status === "sent" && <span className="badge b-green"><span className="bd" />Sent</span>}
                      {h.status === "failed" && <span className="badge b-red"><span className="bd" />Failed</span>}
                      {h.status === "no_recipient" && <span className="badge"><span className="bd" />No recipient</span>}
                      {h.status !== "sent" && h.status !== "failed" && h.status !== "no_recipient" && (
                        <span className="badge b-amber" data-testid={`recall-history-status-${h.id}`}>
                          <span className="bd" />{SEND_REASON_LABELS[h.status] || h.status}
                        </span>
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {h.actual_recipient || h.intended_recipient || "—"}
                      {h.test_mode && h.intended_recipient && h.actual_recipient !== h.intended_recipient && (
                        <span className="t-mut" style={{ marginLeft: 6 }}>(intended {h.intended_recipient})</span>
                      )}
                    </td>
                    <td><span className="t-mut" style={{ fontSize: 11 }}>{h.trigger}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmSend && preview && (
        <Modal open onClose={() => setConfirmSend(false)} icon={Send}
               title="Send Calibration Due email"
               subtitle={`${preview.will_send} customers · ${preview.total_items} items · ${cfg.window_days}-day window`}
               footer={<>
                 <button className="btn ghost" onClick={() => setConfirmSend(false)}>Cancel</button>
                 <button className="btn gold" onClick={sendBlast} data-testid="recall-confirm-send">
                   <Send size={13} /> Confirm &amp; send
                 </button>
               </>}>
          <p style={{ fontSize: 13, marginBottom: 10 }}>
            Each customer will receive a branded PDF with their {cfg.window_days}-day Calibration Due list.
          </p>
          {cfg.test_mode && (
            <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", fontSize: 12.5 }}>
              <b>TEST MODE active</b> — all {preview.will_send} emails will go to <code>{cfg.test_recipient}</code>, not the real customers.
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}



// ============================================================
//  Non-Conforming / Unserviceable notice email — config + history
// ============================================================
function UnserviceableEmail() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/recall/unserviceable/config"),
        api.get("/recall/sends", { params: { limit: 50 } }),
      ]);
      setCfg(c.data);
      setHistory((h.data.items || []).filter((x) => x.trigger === "unserviceable_notice"));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/recall/unserviceable/config", {
        enabled: cfg.enabled,
        subject_template: cfg.subject_template,
        html_template: cfg.html_template,
        cc_email: cfg.cc_email || "",
        test_mode: !!cfg.test_mode,
        test_recipient: cfg.test_recipient || "",
      });
      setCfg(r.data);
      toast.success("Non-Conforming notice settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="card pad" style={{ marginTop: 16 }}><RefreshCw size={14} className="spin" /> Loading…</div>;

  const testBanner = cfg.test_mode && (
    <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", marginBottom: 14, fontSize: 13 }}>
      <AlertTriangle size={15} style={{ verticalAlign: "-3px", marginRight: 6 }} />
      <b>TEST MODE</b> — every email is redirected to <code>{cfg.test_recipient || "(not set)"}</code> instead of the real customer.
      Turn off "Test mode" below once you're ready to send to real customers.
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
      {testBanner}
      <div className="card pad">
        <div className="sec-h">
          <h3>Equipment Non-Conforming notice</h3>
          <AlertTriangle size={18} color="var(--gold-deep)" />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
          Sent on demand from an item's <b>Unserviceable review</b> ("Email notice to customer"). The customer's
          contact (end-user override → primary → any active → customer record) receives a branded email with the
          <b> Equipment Non-Conforming Report</b> PDF attached plus any technician photos. Turn this off here to
          block all non-conforming sends.
        </p>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Sending enabled</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.enabled}
                   onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                   data-testid="unserv-enabled" style={{ width: 16, height: 16 }} />
            {cfg.enabled ? "Yes — admins can email the notice" : "No — sending disabled"}
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 14 }}>
          <div className="fld">
            <label>Test mode</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
              <input type="checkbox" checked={!!cfg.test_mode}
                     onChange={(e) => setCfg({ ...cfg, test_mode: e.target.checked })}
                     data-testid="unserv-test-mode"
                     style={{ width: 16, height: 16 }} />
              {cfg.test_mode ? "On — redirects every send to the test recipient" : "Off — sends go to real customers"}
            </label>
          </div>
          <div className="fld">
            <label>Test recipient</label>
            <input className="input mono" value={cfg.test_recipient || ""}
                   onChange={(e) => setCfg({ ...cfg, test_recipient: e.target.value })}
                   placeholder="you@example.com" data-testid="unserv-test-recipient" />
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Subject template</label>
          <input className="input" value={cfg.subject_template || ""}
                 onChange={(e) => setCfg({ ...cfg, subject_template: e.target.value })}
                 data-testid="unserv-subject" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Placeholders: <code>{"{customer_name}"}</code>, <code>{"{contact_name}"}</code>, <code>{"{item_description}"}</code>, <code>{"{serial_number}"}</code>, <code>{"{issue_number}"}</code>, <code>{"{delivery_note_number}"}</code>, <code>{"{report_number}"}</code>, <code>{"{reason}"}</code>, <code>{"{technician_notes}"}</code>, <code>{"{quote_contacts}"}</code>, <code>{"{today}"}</code>.
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>CC (optional)</label>
          <input className="input mono" value={cfg.cc_email || ""}
                 onChange={(e) => setCfg({ ...cfg, cc_email: e.target.value })}
                 placeholder="heike@ptecal.co.za, elsa@ptecal.co.za" data-testid="unserv-cc" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            One or more addresses, comma-separated. Copied on every real send (not applied while Test mode is on).
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>HTML body template</label>
          <textarea className="input mono" rows={12}
                    value={cfg.html_template || ""}
                    onChange={(e) => setCfg({ ...cfg, html_template: e.target.value })}
                    data-testid="unserv-html"
                    style={{ fontSize: 12, lineHeight: 1.45 }} />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>Inline CSS only. Same placeholders as subject.</div>
        </div>

        <button className="btn gold" onClick={save} disabled={saving} data-testid="unserv-save">
          <Save size={13} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="card pad">
        <div className="sec-h">
          <h3>Recent notices sent</h3>
          <History size={18} color="var(--gold-deep)" />
        </div>
        {history.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>No non-conforming notices sent yet.</p>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>When</th><th>Customer</th><th>Delivery note</th><th>Report</th><th>Status</th><th>Recipient</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="mono t-mut">{fmtDate(h.sent_at)}</td>
                    <td className="t-strong">{h.customer_name}</td>
                    <td className="mono">{h.delivery_note_number || "—"}</td>
                    <td className="mono">{h.report_number || "—"}</td>
                    <td>
                      {h.status === "sent" && <span className="badge b-green"><span className="bd" />Sent</span>}
                      {h.status === "failed" && <span className="badge b-red"><span className="bd" />Failed</span>}
                      {h.status === "no_recipient" && <span className="badge"><span className="bd" />No recipient</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {h.actual_recipient || h.intended_recipient || "—"}
                      {h.test_mode && h.intended_recipient && h.actual_recipient !== h.intended_recipient && (
                        <span className="t-mut" style={{ marginLeft: 6 }}>(intended {h.intended_recipient})</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
// ============================================================
//  PO Follow-up reminder email — SPT-PTE-2026-0062
// ============================================================
function PoFollowupEmail({ canSend }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);
  const [runningNow, setRunningNow] = useState(false);
  const [testItemId, setTestItemId] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, p] = await Promise.all([
        api.get("/recall/po-followup/config"),
        api.get("/recall/sends", { params: { limit: 50 } }),
        api.get("/recall/po-followup/pending"),
      ]);
      setCfg(c.data);
      setHistory((h.data.items || []).filter((x) => x.trigger === "po_followup" || x.trigger === "manual_po_followup"));
      setPending(p.data.groups || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/recall/po-followup/config", {
        enabled: cfg.enabled,
        resend_frequency_days: Number(cfg.resend_frequency_days) || 4,
        subject_template: cfg.subject_template,
        html_template: cfg.html_template,
        cc_email: cfg.cc_email || "",
        test_mode: !!cfg.test_mode,
        test_recipient: cfg.test_recipient || "",
      });
      setCfg(r.data);
      toast.success("PO follow-up settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const r = await api.post("/recall/po-followup/run-now");
      toast.success(`Checked ${r.data.groups_checked} overdue quotation(s)`);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRunningNow(false); }
  };

  const sendTest = async () => {
    if (!testItemId.trim()) { toast.error("Enter a job item ID"); return; }
    setTesting(true);
    try {
      const r = await api.post("/recall/po-followup/send-test", { job_item_id: testItemId.trim() });
      if (r.data.ok) toast.success(`Test reminder sent to ${r.data.to}`);
      else toast.error(r.data.reason || r.data.error || "Not sent");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="card pad" style={{ marginTop: 16 }}><RefreshCw size={14} className="spin" /> Loading…</div>;

  const testBanner = cfg.test_mode && (
    <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", marginBottom: 14, fontSize: 13 }}>
      <AlertTriangle size={15} style={{ verticalAlign: "-3px", marginRight: 6 }} />
      <b>TEST MODE</b> — every reminder is redirected to <code>{cfg.test_recipient || "(not set)"}</code> instead of the real customer.
      Turn off "Test mode" below once you're ready to send to real customers.
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
      {testBanner}
      <div className="card pad">
        <div className="sec-h">
          <h3>PO follow-up reminder</h3>
          <CalendarClock size={18} color="var(--gold-deep)" />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
          When Elsa/Heike capture a Sage Quote Number against a job line with no PO yet, a 4-working-day
          countdown starts automatically. If still no PO by then, the customer's primary contact gets this
          reminder — then it re-sends every <b>{cfg.resend_frequency_days}</b> working day(s) until a PO is
          captured or the quote number is cleared, at which point it stops for good.
        </p>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Sending enabled</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.enabled}
                   onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                   data-testid="po-followup-enabled" style={{ width: 16, height: 16 }} />
            {cfg.enabled ? "Yes — reminders will be sent automatically" : "No — automatic sending disabled"}
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 14, marginBottom: 14 }}>
          <div className="fld">
            <label>Resend every (working days)</label>
            <input className="input" type="number" min={1} max={30} value={cfg.resend_frequency_days}
                   onChange={(e) => setCfg({ ...cfg, resend_frequency_days: e.target.value })}
                   data-testid="po-followup-resend-days" />
          </div>
          <div className="fld">
            <label>Test mode</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
              <input type="checkbox" checked={!!cfg.test_mode}
                     onChange={(e) => setCfg({ ...cfg, test_mode: e.target.checked })}
                     data-testid="po-followup-test-mode"
                     style={{ width: 16, height: 16 }} />
              {cfg.test_mode ? "On" : "Off"}
            </label>
          </div>
          <div className="fld">
            <label>Test recipient</label>
            <input className="input mono" value={cfg.test_recipient || ""}
                   onChange={(e) => setCfg({ ...cfg, test_recipient: e.target.value })}
                   placeholder="you@example.com" data-testid="po-followup-test-recipient" />
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Subject template</label>
          <input className="input" value={cfg.subject_template || ""}
                 onChange={(e) => setCfg({ ...cfg, subject_template: e.target.value })}
                 data-testid="po-followup-subject" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Placeholders: <code>{"{customer_name}"}</code>, <code>{"{contact_name}"}</code>, <code>{"{quote_number}"}</code>, <code>{"{delivery_note_number}"}</code>, <code>{"{items_list}"}</code>, <code>{"{quote_contacts}"}</code>, <code>{"{today}"}</code>.
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>CC (optional)</label>
          <input className="input mono" value={cfg.cc_email || ""}
                 onChange={(e) => setCfg({ ...cfg, cc_email: e.target.value })}
                 placeholder="heike@ptecal.co.za, elsa@ptecal.co.za" data-testid="po-followup-cc" />
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>HTML body template</label>
          <textarea className="input mono" rows={12}
                    value={cfg.html_template || ""}
                    onChange={(e) => setCfg({ ...cfg, html_template: e.target.value })}
                    data-testid="po-followup-html"
                    style={{ fontSize: 12, lineHeight: 1.45 }} />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn gold" onClick={save} disabled={saving} data-testid="po-followup-save">
            <Save size={13} /> {saving ? "Saving…" : "Save settings"}
          </button>
          <button className="btn" onClick={runNow} disabled={runningNow} data-testid="po-followup-run-now">
            <RefreshCw size={13} className={runningNow ? "spin" : ""} /> {runningNow ? "Checking…" : "Run check now"}
          </button>
        </div>
      </div>

      <div className="card pad">
        <div className="sec-h"><h3>Currently overdue / awaiting PO</h3></div>
        {pending.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>No quotations are currently overdue for a follow-up.</p>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Customer</th><th>Quote #</th><th>Items</th></tr></thead>
              <tbody>
                {pending.map((p, i) => (
                  <tr key={i}>
                    <td className="t-strong">{p.customer_name || "—"}</td>
                    <td className="mono">{p.quote_number}</td>
                    <td>{p.item_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canSend && (
        <div className="card pad">
          <div className="sec-h"><h3>Send a test reminder now</h3></div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div className="fld" style={{ flex: 1 }}>
              <label>Job item ID (any line with a quote number captured)</label>
              <input className="input mono" value={testItemId} onChange={(e) => setTestItemId(e.target.value)}
                     placeholder="e.g. 6abf812a5068a9f8e1297762" data-testid="po-followup-test-item-id" />
            </div>
            <button className="btn" onClick={sendTest} disabled={testing} data-testid="po-followup-send-test">
              <Send size={13} /> {testing ? "Sending…" : "Send test"}
            </button>
          </div>
        </div>
      )}

      <div className="card pad">
        <div className="sec-h">
          <h3>Recent follow-ups sent</h3>
          <History size={18} color="var(--gold-deep)" />
        </div>
        {history.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>No PO follow-up reminders sent yet.</p>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>When</th><th>Customer</th><th>Quote #</th><th>Status</th><th>Recipient</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="mono t-mut">{fmtDate(h.sent_at)}</td>
                    <td className="t-strong">{h.customer_name}</td>
                    <td className="mono">{h.quote_number || "—"}</td>
                    <td>
                      {h.status === "sent" && <span className="badge b-green"><span className="bd" />Sent</span>}
                      {h.status === "failed" && <span className="badge b-red"><span className="bd" />Failed</span>}
                      {h.status === "no_recipient" && <span className="badge"><span className="bd" />No recipient</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {h.actual_recipient || h.intended_recipient || "—"}
                      {h.test_mode && h.intended_recipient && h.actual_recipient !== h.intended_recipient && (
                        <span className="t-mut" style={{ marginLeft: 6 }}>(intended {h.intended_recipient})</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function IntakeEmail({ canSend }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [testJobId, setTestJobId] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/recall/intake/config"),
        api.get("/recall/sends", { params: { limit: 50 } }),
      ]);
      setCfg(c.data);
      setHistory((h.data.items || []).filter((x) => x.trigger === "intake_received"));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/recall/intake/config", {
        enabled: cfg.enabled,
        subject_template: cfg.subject_template,
        html_template: cfg.html_template,
        cc_email: cfg.cc_email || "",
        test_mode: !!cfg.test_mode,
        test_recipient: cfg.test_recipient || "",
      });
      setCfg(r.data);
      toast.success("Job Received email settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testJobId.trim()) return toast.error("Paste a job_id to test against");
    setTesting(true);
    try {
      const r = await api.post("/recall/intake/send-test", { job_id: testJobId.trim() });
      if (r.data.ok) toast.success(`Sent to ${r.data.to}${r.data.intended && r.data.intended !== r.data.to ? ` (intended ${r.data.intended})` : ""}`);
      else toast.error(`Skipped: ${r.data.reason || r.data.error || "unknown"}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="card pad" style={{ marginTop: 16 }}><RefreshCw size={14} className="spin" /> Loading…</div>;

  const testBanner = cfg.test_mode && (
    <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", marginBottom: 14, fontSize: 13 }}>
      <AlertTriangle size={15} style={{ verticalAlign: "-3px", marginRight: 6 }} />
      <b>TEST MODE</b> — every email is redirected to <code>{cfg.test_recipient || "(not set)"}</code> instead of the real customer.
      Turn off "Test mode" below once you're ready to send to real customers.
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
      {testBanner}

      <div className="card pad">
        <div className="sec-h">
          <h3>Job Received confirmation email</h3>
          <Inbox size={18} color="var(--gold-deep)" />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
          Fires automatically every time a new job is booked in at receiving — the customer's contact gets a confirmation that their equipment was checked in, with a PDF listing every item received under the delivery note. Recipient resolves the same way as Calibration Due: primary contact → any active contact → customer record's email.
        </p>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Auto-send enabled</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.enabled}
                   onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                   data-testid="intake-enabled"
                   style={{ width: 16, height: 16 }} />
            {cfg.enabled ? "Yes — sends on every new job" : "No — disabled"}
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 14 }}>
          <div className="fld">
            <label>Test mode</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
              <input type="checkbox" checked={!!cfg.test_mode}
                     onChange={(e) => setCfg({ ...cfg, test_mode: e.target.checked })}
                     data-testid="intake-test-mode"
                     style={{ width: 16, height: 16 }} />
              {cfg.test_mode ? "On — redirects every send to the test recipient" : "Off — sends go to real customers"}
            </label>
          </div>
          <div className="fld">
            <label>Test recipient</label>
            <input className="input mono" value={cfg.test_recipient || ""}
                   onChange={(e) => setCfg({ ...cfg, test_recipient: e.target.value })}
                   placeholder="you@example.com" data-testid="intake-test-recipient" />
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Subject template</label>
          <input className="input" value={cfg.subject_template || ""}
                 onChange={(e) => setCfg({ ...cfg, subject_template: e.target.value })}
                 data-testid="intake-subject" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Placeholders: <code>{"{customer_name}"}</code>, <code>{"{contact_name}"}</code>, <code>{"{item_count}"}</code>, <code>{"{delivery_note_number}"}</code>, <code>{"{date_received}"}</code>.
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>CC (optional)</label>
          <input className="input mono" value={cfg.cc_email || ""}
                 onChange={(e) => setCfg({ ...cfg, cc_email: e.target.value })}
                 placeholder="heike@ptecal.co.za, elsa@ptecal.co.za" data-testid="intake-cc" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            One or more addresses, comma-separated. Copied on every real send (not applied while Test mode is on).
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>HTML body template</label>
          <textarea className="input mono" rows={10}
                    value={cfg.html_template || ""}
                    onChange={(e) => setCfg({ ...cfg, html_template: e.target.value })}
                    data-testid="intake-html"
                    style={{ fontSize: 12, lineHeight: 1.45 }} />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Inline CSS only. Same placeholders as subject.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn gold" onClick={save} disabled={saving} data-testid="intake-save">
            <Save size={13} /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      {canSend && (
        <div className="card pad">
          <div className="sec-h">
            <h3>Send a test against an existing job</h3>
            <Send size={18} color="var(--gold-deep)" />
          </div>
          <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
            Paste a <code>job_id</code> (visible on any job's URL) to fire the intake email against it. Uses the same code path as the auto-send. Honours TEST MODE if enabled.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="input mono" placeholder="job_id (e.g. 6a1d4173db906fc9d2…)"
                   value={testJobId} onChange={(e) => setTestJobId(e.target.value)}
                   data-testid="intake-test-jobid"
                   style={{ flex: 1, minWidth: 320 }} />
            <button className="btn gold" onClick={sendTest} disabled={testing || !testJobId.trim()}
                    data-testid="intake-test-send">
              <Send size={13} /> {testing ? "Sending…" : "Send test"}
            </button>
          </div>
        </div>
      )}

      <div className="card pad">
        <div className="sec-h">
          <h3>Recent intake emails</h3>
          <History size={18} color="var(--gold-deep)" />
        </div>
        {history.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>No Job Received emails sent yet.</p>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>When</th><th>Customer</th><th>Delivery note</th><th>Items</th><th>Status</th><th>Recipient</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="mono t-mut">{fmtDate(h.sent_at)}</td>
                    <td className="t-strong">{h.customer_name}</td>
                    <td className="mono">{h.delivery_note_number || "—"}</td>
                    <td className="mono">{h.item_count}</td>
                    <td>
                      {h.status === "sent" && <span className="badge b-green"><span className="bd" />Sent</span>}
                      {h.status === "failed" && <span className="badge b-red"><span className="bd" />Failed</span>}
                      {h.status === "no_recipient" && <span className="badge"><span className="bd" />No recipient</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {h.actual_recipient || h.intended_recipient || "—"}
                      {h.test_mode && h.intended_recipient && h.actual_recipient !== h.intended_recipient && (
                        <span className="t-mut" style={{ marginLeft: 6 }}>(intended {h.intended_recipient})</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  Standards Due digest — SPT-PTE Standards feature
// ============================================================
function StandardsDueEmail({ canSend }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [due, setDue] = useState([]);
  const [runningNow, setRunningNow] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, d] = await Promise.all([
        api.get("/standards/recall/config"),
        api.get("/recall/sends", { params: { limit: 50 } }),
        api.get("/standards/recall/due"),
      ]);
      setCfg(c.data);
      setHistory((h.data.items || []).filter((x) => x.trigger === "standards_due" || x.trigger === "manual_standards_due"));
      setDue(d.data.items || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/standards/recall/config", {
        enabled: cfg.enabled,
        window_days: Number(cfg.window_days) || 90,
        resend_frequency_days: Number(cfg.resend_frequency_days) || 7,
        recipients: String(cfg.recipients_text ?? (cfg.recipients || []).join(", ")).split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        subject_template: cfg.subject_template,
        html_template: cfg.html_template,
        test_mode: !!cfg.test_mode,
        test_recipient: cfg.test_recipient || "",
      });
      setCfg(r.data);
      toast.success("Standards Due settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const r = await api.post("/standards/recall/run-now");
      toast.success(r.data.skipped ? `Not sent: ${r.data.reason}` : "Digest check complete");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRunningNow(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/standards/recall/send-test");
      if (r.data.ok) toast.success(`Test digest sent to ${r.data.to.join(", ")} (${r.data.item_count} item(s))`);
      else toast.error(r.data.reason || r.data.error || "Not sent");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="card pad" style={{ marginTop: 16 }}><RefreshCw size={14} className="spin" /> Loading…</div>;

  const testBanner = cfg.test_mode && (
    <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", marginBottom: 14, fontSize: 13 }}>
      <AlertTriangle size={15} style={{ verticalAlign: "-3px", marginRight: 6 }} />
      <b>TEST MODE</b> — every digest is redirected to <code>{cfg.test_recipient || "(not set)"}</code> instead of the configured recipients.
      Turn off "Test mode" below once you're ready for real sends.
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
      {testBanner}
      <div className="card pad">
        <div className="sec-h">
          <h3>PTE Standards — calibration due digest</h3>
          <Ruler size={18} color="var(--gold-deep)" />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
          The most important Standards feature: gives the team a heads-up <b>{cfg.window_days} days</b> before one of
          PTE's own standards/equipment is due for calibration — whether it needs to go to a SANAS subcontractor or
          be done in-house — so nothing lapses and PTE stays a traceable calibration laboratory. Currently
          <b> {due.length}</b> item(s) due/overdue.
        </p>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Sending enabled</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.enabled}
                   onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                   data-testid="standards-due-enabled" style={{ width: 16, height: 16 }} />
            {cfg.enabled ? "Yes — digest will resend automatically" : "No — automatic sending disabled"}
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 14, marginBottom: 14 }}>
          <div className="fld">
            <label>Heads-up window (days)</label>
            <input className="input" type="number" min={1} max={365} value={cfg.window_days}
                   onChange={(e) => setCfg({ ...cfg, window_days: e.target.value })}
                   data-testid="standards-due-window-days" />
          </div>
          <div className="fld">
            <label>Resend every (days)</label>
            <input className="input" type="number" min={1} max={30} value={cfg.resend_frequency_days}
                   onChange={(e) => setCfg({ ...cfg, resend_frequency_days: e.target.value })}
                   data-testid="standards-due-resend-days" />
          </div>
          <div className="fld">
            <label>Test mode</label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13 }}>
              <input type="checkbox" checked={!!cfg.test_mode}
                     onChange={(e) => setCfg({ ...cfg, test_mode: e.target.checked })}
                     data-testid="standards-due-test-mode"
                     style={{ width: 16, height: 16 }} />
              {cfg.test_mode ? "On" : "Off"}
            </label>
          </div>
          <div className="fld">
            <label>Test recipient</label>
            <input className="input mono" value={cfg.test_recipient || ""}
                   onChange={(e) => setCfg({ ...cfg, test_recipient: e.target.value })}
                   placeholder="you@example.com" data-testid="standards-due-test-recipient" />
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Recipients</label>
          <input className="input mono" value={cfg.recipients_text ?? (cfg.recipients || []).join(", ")}
                 onChange={(e) => setCfg({ ...cfg, recipients_text: e.target.value })}
                 placeholder="adolf@ptecal.co.za, heike@ptecal.co.za" data-testid="standards-due-recipients" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            One or more internal staff addresses, comma-separated — e.g. Adolf, who manages the Standards flow.
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>Subject template</label>
          <input className="input" value={cfg.subject_template || ""}
                 onChange={(e) => setCfg({ ...cfg, subject_template: e.target.value })}
                 data-testid="standards-due-subject" />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Placeholders: <code>{"{count}"}</code>, <code>{"{window_days}"}</code>.
          </div>
        </div>

        <div className="fld" style={{ marginBottom: 14 }}>
          <label>HTML body template</label>
          <textarea className="input mono" rows={10}
                    value={cfg.html_template || ""}
                    onChange={(e) => setCfg({ ...cfg, html_template: e.target.value })}
                    data-testid="standards-due-html"
                    style={{ fontSize: 12, lineHeight: 1.45 }} />
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
            Also uses <code>{"{overdue_table}"}</code> and <code>{"{due_soon_table}"}</code> — auto-built tables of the due items.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn gold" onClick={save} disabled={saving} data-testid="standards-due-save">
            <Save size={13} /> {saving ? "Saving…" : "Save settings"}
          </button>
          <button className="btn" onClick={runNow} disabled={runningNow} data-testid="standards-due-run-now">
            <RefreshCw size={13} className={runningNow ? "spin" : ""} /> {runningNow ? "Checking…" : "Run check now"}
          </button>
          <button className="btn" onClick={sendTest} disabled={testing} data-testid="standards-due-send-test">
            <Send size={13} /> {testing ? "Sending…" : "Send test digest now"}
          </button>
        </div>
      </div>

      <div className="card pad">
        <div className="sec-h">
          <h3>Due / overdue right now ({due.length})</h3>
          <CalendarClock size={18} color="var(--gold-deep)" />
        </div>
        {due.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>Nothing due within the current window.</p>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Description</th><th>Area</th><th>Serial</th><th>Due</th><th>Status</th></tr></thead>
              <tbody>
                {due.map((it) => (
                  <tr key={it.id}>
                    <td className="t-strong">{it.description}</td>
                    <td>{it.area || "—"}</td>
                    <td className="mono">{it.serial_number || "—"}</td>
                    <td className="mono">{fmtDate(it.calibration_due_date)}</td>
                    <td>{it.location_status === "sent_to_subcontractor" ? `Sent — ${it.subcontractor_name || ""}` : "In-house"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card pad">
        <div className="sec-h">
          <h3>Recent digests sent</h3>
          <History size={18} color="var(--gold-deep)" />
        </div>
        {history.length === 0 ? (
          <p className="t-mut" style={{ fontSize: 13 }}>No Standards Due digests sent yet.</p>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>When</th><th>Items</th><th>Status</th><th>Recipient</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="mono t-mut">{fmtDate(h.sent_at)}</td>
                    <td className="mono">{h.item_count ?? "—"}</td>
                    <td>
                      {h.status === "sent" && <span className="badge b-green"><span className="bd" />Sent</span>}
                      {h.status === "failed" && <span className="badge b-red"><span className="bd" />Failed</span>}
                      {h.status === "no_recipient" && <span className="badge"><span className="bd" />No recipient</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{h.actual_recipient || h.intended_recipient || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

